import type { LocalAttachment } from '@/api/types/MessagingApi'
import * as DocumentPicker from 'expo-document-picker'
import * as ImagePicker from 'expo-image-picker'
import * as Linking from 'expo-linking'
import Toast from 'react-native-toast-message'

/** What the server accepts, and how big. Mirrors messaging-kapi's ChatAttachmentService. */
export const ATTACHMENT_TYPES = [
	'image/jpeg',
	'image/png',
	'image/gif',
	'video/mp4',
	'application/pdf',
	'text/plain',
	'text/csv',
]
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
export const MAX_ATTACHMENTS = 10

const tooBig = () =>
	Toast.show({
		type: 'error',
		text1: 'That file is over 10 MB, try a smaller one',
	})
const wrongType = () =>
	Toast.show({
		type: 'error',
		text1: "We can't send that kind of file — photos, videos, PDFs and text files work",
	})

const filenameOf = (uri: string, fallback: string) => uri.split('/').pop() || fallback

/** One picked image or video as the attachment the message will carry, or null if it will not do. */
const fromImagePicker = (asset: ImagePicker.ImagePickerAsset): LocalAttachment | null => {
	const contentType =
		asset.mimeType ?? (asset.type === 'video' ? 'video/mp4' : 'image/jpeg')
	if (!ATTACHMENT_TYPES.includes(contentType)) {
		wrongType()
		return null
	}
	if (asset.fileSize && asset.fileSize > MAX_ATTACHMENT_BYTES) {
		tooBig()
		return null
	}
	return {
		uri: asset.uri,
		filename: asset.fileName ?? filenameOf(asset.uri, 'photo.jpg'),
		contentType,
		size: asset.fileSize ?? undefined,
	}
}

const fromDocumentPicker = (
	asset: DocumentPicker.DocumentPickerAsset
): LocalAttachment | null => {
	const contentType = asset.mimeType ?? ''
	if (!ATTACHMENT_TYPES.includes(contentType)) {
		wrongType()
		return null
	}
	if (asset.size && asset.size > MAX_ATTACHMENT_BYTES) {
		tooBig()
		return null
	}
	return {
		uri: asset.uri,
		filename: asset.name,
		contentType,
		size: asset.size ?? undefined,
	}
}

export const pickFromLibrary = async (): Promise<LocalAttachment[]> => {
	const result = await ImagePicker.launchImageLibraryAsync({
		mediaTypes: ['images', 'videos'],
		allowsMultipleSelection: true,
		selectionLimit: MAX_ATTACHMENTS,
		quality: 0.9,
	})
	if (result.canceled) return []
	return result.assets.map(fromImagePicker).filter((a): a is LocalAttachment => !!a)
}

export const takePhoto = async (): Promise<LocalAttachment[]> => {
	const permission = await ImagePicker.requestCameraPermissionsAsync()
	if (!permission.granted) {
		Toast.show({
			type: 'error',
			text1: 'Camera is off for Wally, tap to turn it on in Settings',
			onPress: () => Linking.openSettings(),
		})
		return []
	}
	const result = await ImagePicker.launchCameraAsync({ quality: 0.9 })
	if (result.canceled) return []
	return result.assets.map(fromImagePicker).filter((a): a is LocalAttachment => !!a)
}

export const pickDocument = async (): Promise<LocalAttachment[]> => {
	const result = await DocumentPicker.getDocumentAsync({
		type: ATTACHMENT_TYPES,
		multiple: true,
		copyToCacheDirectory: true,
	})
	if (result.canceled) return []
	return result.assets.map(fromDocumentPicker).filter((a): a is LocalAttachment => !!a)
}
